import { createContext, useContext, useState, useEffect } from 'react';

const AccessibilityContext = createContext({});

const STORAGE_KEY = 'qude_a11y';

const DEFAULTS = {
  darkMode:      true,
  textSize:      'md',
  colorblind:    'none',
  reduceMotion:  false,
  rtl:           false,
  language:      'en',
  spoilerShield: false,
};

const TEXT_SCALE = { sm: '14px', md: '16px', lg: '18px', xl: '20px' };

const COLORBLIND_FILTERS = {
  none:          '',
  protanopia:    'url(#protanopia)',
  deuteranopia:  'url(#deuteranopia)',
  tritanopia:    'url(#tritanopia)',
};

export function AccessibilityProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const merged = stored ? { ...DEFAULTS, ...JSON.parse(stored) } : DEFAULTS;
      return { ...merged, darkMode: true };
    } catch {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {}
  }, [settings]);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.documentElement.classList.remove('light-mode');
  }, [settings.darkMode]);

  useEffect(() => {
    document.documentElement.style.fontSize = TEXT_SCALE[settings.textSize] || '16px';
  }, [settings.textSize]);

  useEffect(() => {
    document.documentElement.setAttribute('dir', settings.rtl ? 'rtl' : 'ltr');
    document.documentElement.classList.toggle('rtl', settings.rtl);
  }, [settings.rtl]);

  useEffect(() => {
    document.documentElement.classList.toggle('reduce-motion', settings.reduceMotion);
    const id = 'qude-reduce-motion';
    let style = document.getElementById(id);
    if (settings.reduceMotion) {
      if (!style) {
        style = document.createElement('style');
        style.id = id;
        style.textContent = '*, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; scroll-behavior: auto !important; }';
        document.head.appendChild(style);
      }
    } else {
      if (style) style.remove();
    }
  }, [settings.reduceMotion]);

  useEffect(() => {
    const id = 'qude-colorblind-svg';
    const existing = document.getElementById(id);
    if (existing) existing.remove();

    if (settings.colorblind === 'none') {
      document.body.style.filter = '';
      return;
    }

    const matrices = {
      protanopia:   '0.567,0.433,0,0,0 0.558,0.442,0,0,0 0,0.242,0.758,0,0 0,0,0,1,0',
      deuteranopia: '0.625,0.375,0,0,0 0.7,0.3,0,0,0 0,0.3,0.7,0,0 0,0,0,1,0',
      tritanopia:   '0.95,0.05,0,0,0 0,0.433,0.567,0,0 0,0.475,0.525,0,0 0,0,0,1,0',
    };

    const matrix = matrices[settings.colorblind];
    if (!matrix) return;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = id;
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
    svg.innerHTML = '<defs><filter id="protanopia"><feColorMatrix type="matrix" values="' + matrices.protanopia + '" /></filter><filter id="deuteranopia"><feColorMatrix type="matrix" values="' + matrices.deuteranopia + '" /></filter><filter id="tritanopia"><feColorMatrix type="matrix" values="' + matrices.tritanopia + '" /></filter></defs>';
    document.body.insertBefore(svg, document.body.firstChild);
    document.body.style.filter = COLORBLIND_FILTERS[settings.colorblind] || '';
  }, [settings.colorblind]);

  const update = (key, value) => {
    if (key === 'darkMode') return;
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <AccessibilityContext.Provider value={{ settings, update }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useA11y() {
  return useContext(AccessibilityContext);
}

export default AccessibilityContext;
